import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { MessageCircle, RefreshCw, ShieldCheck } from 'lucide-react'
import { apiClient, ApiClientError } from '@/lib/api/client'
import type { Consignment, Customer, Inquiry } from '@/lib/api/types'
import type {
  WhatsAppBinding,
  WhatsAppGroup,
  WhatsAppMessage,
  WhatsAppOutbox,
  WhatsAppReviewGroups,
  WhatsAppStatus,
} from '@/lib/api/whatsapp-types'
import { PageHeader } from '@/components/layout/page-header'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Combobox } from '@/components/ui/combobox'
import { Textarea } from '@/components/ui/textarea'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { ErrorState } from '@/src/components/ErrorState'
import { LoadingState } from '@/src/components/LoadingState'
import { WhatsAppBeaconReview } from '@/src/components/WhatsAppBeaconReview'
import { useAsyncData } from '@/src/hooks/useAsyncData'

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'The request could not be completed.'
}
function dateLabel(value: string | null | undefined) {
  return value ? new Date(value).toLocaleString() : 'Not yet recorded'
}
function StatusNote({ children, error = false }: { children: React.ReactNode; error?: boolean }) {
  return (
    <Alert variant={error ? 'destructive' : 'default'}>
      <AlertDescription role="status">{children}</AlertDescription>
    </Alert>
  )
}

/**
 * "15:00", or "24 Oct 15:00" on another day: Beacon's digest times on Kampala's clock,
 * to the nearest minute (a window ends just before the run that reads it).
 */
function kampalaTime(value: string) {
  const at = new Date(new Date(value).getTime() + 30_000)
  const day = (date: Date) =>
    date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Africa/Kampala' })
  const time = at.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'Africa/Kampala',
  })
  return day(at) === day(new Date()) ? time : `${day(at)} ${time}`
}

const DIGEST_RESULTS: Record<string, string> = {
  NO_NEW_MESSAGES: 'No new messages',
  NOTHING_MEANINGFUL: 'Nothing to act on',
  NO_CHANGES: 'No changes proposed',
  WAITING_FOR_MEDIA: 'Waiting for attachments',
  FAILED: 'Could not be read',
}

function digestResult(digest: NonNullable<WhatsAppBinding['beacon_digest']>) {
  if (digest.last_result === 'PROPOSALS') {
    const count = digest.last_proposals ?? 0
    return `${count} proposal${count === 1 ? '' : 's'}`
  }
  return (digest.last_result && DIGEST_RESULTS[digest.last_result]) || 'Read'
}

/** When Beacon last read a group, what came of it, and when it reads next. */
function BeaconDigestStatus({ binding }: { binding: WhatsAppBinding }) {
  const digest = binding.beacon_digest
  if (!digest || !binding.active) return null
  if (!digest.active_group)
    return (
      <p className="text-sm text-muted-foreground">
        Inactive: no open inquiry. Beacon does not read this group; it only drafts a reply when
        someone mentions or replies to Xtellar.
      </p>
    )
  if (digest.schedule === 'realtime')
    return <p className="text-sm text-muted-foreground">Beacon reads each message as it arrives.</p>
  return (
    <div className="flex flex-col gap-1 text-sm">
      <p>
        Beacon digest:{' '}
        {digest.last_run_at
          ? `last run ${kampalaTime(digest.last_run_at)} · ${digestResult(digest)}`
          : 'not run yet'}
        {digest.next_run_at ? ` · next run ${kampalaTime(digest.next_run_at)}` : ''}
      </p>
      {digest.last_window?.summary && (
        <p className="break-words text-muted-foreground">
          {kampalaTime(digest.last_window.start)}–{kampalaTime(digest.last_window.end)}:{' '}
          {digest.last_window.summary}
        </p>
      )}
    </div>
  )
}
function Choice({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  options: { id: string; label: string }[]
  placeholder: string
  disabled?: boolean
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Combobox
        id={id}
        aria-label={label}
        value={value}
        onValueChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        options={options.map((option) => ({ value: option.id, label: option.label }))}
        renderOption={(option) => <span className="helm-private">{option.label}</span>}
      />
    </Field>
  )
}

function pairingError(error: unknown, operation: 'start' | 'qr' | 'status') {
  const guidance: Record<string, string> = {
    WHATSAPP_NOT_STARTED: 'Start pairing first, then wait until WhatsApp is ready to scan.',
    WHATSAPP_SESSION_STOPPED:
      'The connection is stopped. Start pairing, then wait until WhatsApp is ready to scan.',
    WHATSAPP_SESSION_STARTING:
      'WhatsApp is still starting. Wait for the connection status to become ready to scan.',
    WHATSAPP_SESSION_FAILED:
      'The pairing attempt ended. Start pairing again, wait until it is ready to scan, then show the current QR.',
    WHATSAPP_ALREADY_CONNECTED: 'WhatsApp is already connected. A pairing QR is not needed.',
    WHATSAPP_CONFIG_REVIEW_REQUIRED:
      'An operator must review the WhatsApp connection configuration before pairing. Refresh after that review.',
    WHATSAPP_SESSION_NOT_READY:
      'The WhatsApp connection needs an operator review before pairing. Refresh connection for its latest state.',
    WHATSAPP_RECOVERY_REQUIRES_REVIEW:
      'Automatic pairing recovery stopped because an account may already be linked. Refresh connection and ask an operator to review it.',
    WHATSAPP_QR_NOT_READY:
      'The current pairing QR is not ready. Refresh connection, then show the current QR when it is ready to scan.',
  }
  if (error instanceof ApiClientError && guidance[error.code]) return guidance[error.code]
  if (!(error instanceof ApiClientError) || error.status >= 500) {
    const action =
      operation === 'start'
        ? 'start WhatsApp pairing'
        : operation === 'qr'
          ? 'show the current pairing QR'
          : 'refresh the WhatsApp connection'
    return `Could not ${action}. Refresh connection for its latest state. If it remains failed or unavailable, an operator must check the connection service before you try again.`
  }
  return error.message
}

const PAIRING_WINDOW_MS = 10 * 60_000
const PAIRING_POLL_MS = 2_000
const PAIRING_QR_REFRESH_MS = 5_000
const PAIRING_MAX_RECOVERIES = 2
const PAIRING_MAX_QR_FAILURES = 5

function SessionCard({ status, onChanged }: { status: WhatsAppStatus; onChanged: () => void }) {
  type PairingAttempt = {
    deadline: number
    recoveries: number
    lastState: string
    lastQrAt: number
    qrFailures: number
    cancelled: boolean
  }
  const [sessionState, setSessionState] = useState(status.status)
  const [busy, setBusy] = useState(false)
  const [active, setActive] = useState(false)
  const [qr, setQr] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pollRun, setPollRun] = useState(0)
  const attemptRef = useRef<PairingAttempt | null>(null)
  const onChangedRef = useRef(onChanged)
  useEffect(() => {
    onChangedRef.current = onChanged
  }, [onChanged])
  useEffect(() => {
    if (!attemptRef.current) setSessionState(status.status)
  }, [status.status])

  const stopAttempt = useCallback((message: string | null = null) => {
    if (attemptRef.current) attemptRef.current.cancelled = true
    attemptRef.current = null
    setActive(false)
    setQr(null)
    setNote(message)
  }, [])

  // Pairing intent is local and ephemeral: leaving/hiding the page cancels every automatic action.
  useEffect(() => {
    function hidden() {
      if (document.visibilityState === 'hidden' && attemptRef.current) {
        stopAttempt(
          'Pairing view paused while this page was hidden. Show the current QR to continue.',
        )
      }
    }
    document.addEventListener('visibilitychange', hidden)
    return () => {
      document.removeEventListener('visibilitychange', hidden)
      if (attemptRef.current) attemptRef.current.cancelled = true
      attemptRef.current = null
    }
  }, [stopAttempt])

  useEffect(() => {
    const attempt = attemptRef.current
    if (!active || !attempt) return
    let effectCancelled = false
    let timer: number | undefined
    const timedOut =
      'Pairing view timed out after 10 minutes. Start a new pairing attempt when you are ready to scan.'
    const current = () => {
      if (effectCancelled || attempt.cancelled || attemptRef.current !== attempt) return false
      if (document.visibilityState === 'hidden') {
        stopAttempt(
          'Pairing view paused while this page was hidden. Show the current QR to continue.',
        )
        return false
      }
      if (Date.now() >= attempt.deadline) {
        stopAttempt(timedOut)
        return false
      }
      return true
    }
    const deadlineTimer = window.setTimeout(
      () => {
        if (!effectCancelled && attemptRef.current === attempt) stopAttempt(timedOut)
      },
      Math.max(0, attempt.deadline - Date.now()),
    )
    function fail(message: string) {
      if (!current()) return
      stopAttempt()
      setError(message)
    }
    async function tick() {
      if (!current()) return
      let operation: 'status' | 'qr' | 'start' = 'status'
      try {
        const next = await apiClient.getWhatsAppStatus()
        if (!current()) return
        const previousState = attempt!.lastState
        attempt!.lastState = next.status
        setSessionState(next.status)
        if (next.status !== 'SCAN_QR_CODE') setQr(null)
        if (next.status === 'WORKING') {
          stopAttempt()
          onChangedRef.current()
          return
        }
        if (next.status === 'FAILED') {
          if (previousState !== 'SCAN_QR_CODE' || next.account_present !== false) {
            fail(
              'Automatic pairing recovery stopped. The connection failed or its account state is uncertain; an operator must review it before another restart.',
            )
            return
          }
          if (attempt!.recoveries >= PAIRING_MAX_RECOVERIES) {
            fail(
              'Automatic pairing recovery stopped after two restarts. An operator must check the connection before starting another attempt.',
            )
            return
          }
          attempt!.recoveries++
          attempt!.lastQrAt = 0
          attempt!.qrFailures = 0
          setNote(
            'Pairing ended before a phone connected. Preparing a current QR for another attempt.',
          )
          operation = 'start'
          const recovered = await apiClient.startWhatsAppSession({ recover_pairing: true })
          if (!current()) return
          attempt!.lastState = recovered.status
          setSessionState(recovered.status)
          if (recovered.status === 'WORKING') {
            stopAttempt()
            onChangedRef.current()
            return
          }
          if (!['STARTING', 'SCAN_QR_CODE'].includes(recovered.status)) {
            fail(
              'Automatic pairing recovery did not become ready. An operator must check the connection before trying again.',
            )
            return
          }
        } else if (!['STARTING', 'SCAN_QR_CODE'].includes(next.status)) {
          fail(
            'Pairing paused because the connection is not ready. Refresh connection; an operator must review it if it remains unavailable.',
          )
          return
        }
        if (
          attempt!.lastState === 'SCAN_QR_CODE' &&
          Date.now() - attempt!.lastQrAt >= PAIRING_QR_REFRESH_MS
        ) {
          try {
            operation = 'qr'
            const result = await apiClient.getWhatsAppQr()
            if (!current()) return
            if (!/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/.test(result.qr))
              throw new Error('Invalid pairing image')
            setQr(result.qr)
            attempt!.lastQrAt = Date.now()
            attempt!.qrFailures = 0
            setNote(null)
          } catch (error) {
            if (!current()) return
            setQr(null)
            // Read state before deciding whether an expired unlinked attempt can be restarted.
            if (
              !(error instanceof ApiClientError) ||
              ![
                'WHATSAPP_SESSION_FAILED',
                'WHATSAPP_SESSION_STARTING',
                'WHATSAPP_QR_NOT_READY',
                'WHATSAPP_ALREADY_CONNECTED',
              ].includes(error.code)
            )
              throw error
            attempt!.qrFailures++
            if (attempt!.qrFailures >= PAIRING_MAX_QR_FAILURES) {
              fail(
                'A current pairing QR could not be prepared after repeated checks. An operator must review the connection before trying again.',
              )
              return
            }
          }
        }
        if (current()) timer = window.setTimeout(() => void tick(), PAIRING_POLL_MS)
      } catch (error) {
        fail(pairingError(error, operation))
      }
    }
    void tick()
    return () => {
      effectCancelled = true
      window.clearTimeout(timer)
      window.clearTimeout(deadlineTimer)
    }
  }, [active, pollRun, stopAttempt])

  async function begin(kind: 'start' | 'qr') {
    stopAttempt()
    setBusy(true)
    setError(null)
    const attempt: PairingAttempt = {
      deadline: Date.now() + PAIRING_WINDOW_MS,
      recoveries: 0,
      lastState: sessionState,
      lastQrAt: 0,
      qrFailures: 0,
      cancelled: false,
    }
    attemptRef.current = attempt
    try {
      const result =
        kind === 'start' ? await apiClient.startWhatsAppSession() : { status: sessionState }
      if (
        attempt.cancelled ||
        attemptRef.current !== attempt ||
        document.visibilityState === 'hidden'
      )
        return
      if (Date.now() >= attempt.deadline) {
        stopAttempt(
          'Pairing view timed out after 10 minutes. Start a new pairing attempt when you are ready to scan.',
        )
        return
      }
      setSessionState(result.status)
      attempt.lastState = result.status
      if (result.status === 'WORKING') {
        stopAttempt()
        onChangedRef.current()
      } else if (['STARTING', 'SCAN_QR_CODE'].includes(result.status)) {
        setActive(true)
        setPollRun((value) => value + 1)
      } else {
        stopAttempt()
      }
    } catch (error) {
      if (!attempt.cancelled && attemptRef.current === attempt) {
        stopAttempt()
        setError(pairingError(error, kind))
      }
    } finally {
      setBusy(false)
    }
  }

  async function refreshConnection() {
    setError(null)
    setNote(null)
    if (attemptRef.current) {
      setPollRun((value) => value + 1)
      return
    }
    setBusy(true)
    try {
      const result = await apiClient.getWhatsAppStatus()
      setSessionState(result.status)
      setQr(null)
      onChangedRef.current()
    } catch (error) {
      setError(pairingError(error, 'status'))
    } finally {
      setBusy(false)
    }
  }
  function refreshQr() {
    if (attemptRef.current) {
      attemptRef.current.lastQrAt = 0
      setPollRun((value) => value + 1)
    } else void begin('qr')
  }
  const guidance: Record<string, { title: string; description: string }> = {
    FAILED: {
      title: 'The WhatsApp session failed.',
      description:
        'An earlier pairing code may have expired. Choose Start pairing once to prepare a current QR. If recovery stops or it fails again, ask an operator to check the connection service.',
    },
    STARTING: {
      title: 'WhatsApp is starting.',
      description:
        'Wait for the current QR to appear. While this pairing view is active, Helm checks the connection and prepares the code automatically.',
    },
    SCAN_QR_CODE: {
      title: 'WhatsApp is ready to scan.',
      description:
        'On the intended test phone, open WhatsApp → Linked devices → Link a device. Start the pairing view below and scan the current QR immediately.',
    },
    WORKING: {
      title: 'WhatsApp is connected.',
      description:
        'A pairing QR is not needed. Verify the intended account on its phone before connecting customer groups.',
    },
    STOPPED: {
      title: 'The WhatsApp connection is stopped.',
      description: 'Choose Start pairing to prepare and display a current QR.',
    },
    NOT_STARTED: {
      title: 'The WhatsApp connection has not started.',
      description: 'Choose Start pairing to prepare and display a current QR.',
    },
    UNAVAILABLE: {
      title: 'The WhatsApp connection service is unavailable.',
      description:
        'Refresh connection. If it remains unavailable, an operator must check the service before pairing can continue.',
    },
  }
  const stateGuidance = guidance[sessionState]
  const configured = status.enabled && status.configured
  const canStart =
    configured && !active && ['NOT_STARTED', 'STOPPED', 'FAILED', 'STARTING'].includes(sessionState)
  const canShowQr = configured && sessionState === 'SCAN_QR_CODE'
  const visibleQr = canShowQr && active ? qr : null
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="size-5" />
          Business phone connection
        </CardTitle>
        <CardDescription>
          Use the separate internal test number first. Linking the business phone is an
          administrator action.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant="outline">{sessionState}</Badge>
          <span className="text-sm">Session: {status.session}</span>
          <span className="text-sm text-muted-foreground">Engine: {status.engine}</span>
        </div>
        {!configured ? (
          <StatusNote>
            WhatsApp is not configured in this environment. An operator must complete the server
            configuration before pairing.
          </StatusNote>
        ) : stateGuidance ? (
          <StatusNote error={sessionState === 'FAILED' || sessionState === 'UNAVAILABLE'}>
            <strong className="block">{stateGuidance.title}</strong>
            {stateGuidance.description}
          </StatusNote>
        ) : (
          <StatusNote>
            The connection needs an operator review before pairing. Refresh connection for its
            latest state.
          </StatusNote>
        )}
        <p className="text-sm text-muted-foreground">
          Keep the phone and linked-device session under Xtellar control. An unofficial WhatsApp
          connection can require relinking or be restricted. Each customer uses one dedicated group,
          shared across their inquiries.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button disabled={busy || !canStart} onClick={() => void begin('start')}>
            {sessionState === 'STARTING' ? 'Continue pairing' : 'Start pairing'}
          </Button>
          <Button variant="outline" disabled={busy || !canShowQr} onClick={refreshQr}>
            {visibleQr ? 'Refresh pairing QR' : 'Show pairing QR'}
          </Button>
          {active && (
            <Button
              variant="outline"
              onClick={() =>
                stopAttempt(
                  'Pairing view paused. Show the current QR when you are ready to continue.',
                )
              }
            >
              {visibleQr ? 'Hide pairing QR' : 'Pause pairing view'}
            </Button>
          )}
          <Button variant="outline" disabled={busy} onClick={() => void refreshConnection()}>
            <RefreshCw data-icon="inline-start" />
            Refresh connection
          </Button>
        </div>
        {active && (
          <p className="text-sm text-muted-foreground">
            Pairing view active for up to 10 minutes. The QR refreshes automatically while this page
            stays visible. Expired unlinked attempts may be restarted at most twice. Hide this view
            to stop automatic checks and recovery.
          </p>
        )}
        {visibleQr && (
          <div className="flex flex-col items-start gap-2">
            <img
              src={visibleQr}
              alt="Private WhatsApp linked-device pairing QR"
              className="size-64 rounded border bg-white p-3"
              referrerPolicy="no-referrer"
            />
            <p className="text-sm text-muted-foreground">
              Scan the current code now. WhatsApp rotates codes quickly; Helm refreshes this view
              automatically. Keep this private pairing credential out of screenshots and recordings.
            </p>
          </div>
        )}
        {note && <StatusNote>{note}</StatusNote>}
        {error && <StatusNote error>{error}</StatusNote>}
      </CardContent>
    </Card>
  )
}

function GroupProvisioning({
  customers,
  bindings,
  ready,
  onChanged,
}: {
  customers: Customer[]
  bindings: WhatsAppBinding[]
  ready: boolean
  onChanged: () => void
}) {
  const [groups, setGroups] = useState<WhatsAppGroup[] | null>(null)
  const [customerId, setCustomerId] = useState('')
  const [groupId, setGroupId] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const missing = customers.filter(
    (customer) =>
      !bindings.some((binding) => binding.customer_id === customer.id && binding.active),
  )
  const availableGroups = (groups ?? []).filter(
    (group) =>
      !group.binding_id &&
      !group.customer_id &&
      !bindings.some((binding) => binding.group_id === group.id),
  )
  const selectedGroup = availableGroups.find((group) => group.id === groupId)
  const validCustomer = missing.some((customer) => customer.id === customerId)
  async function discover() {
    setBusy(true)
    setError(null)
    try {
      setGroups(await apiClient.listWhatsAppGroups())
      setGroupId('')
      setConfirmed(false)
    } catch (error) {
      setError(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  async function bind() {
    if (!validCustomer || !selectedGroup || !confirmed) return
    setBusy(true)
    setError(null)
    try {
      await apiClient.bindWhatsAppGroup({
        customer_id: customerId,
        group_id: groupId,
        participant_notice_confirmed: true,
      })
      setCustomerId('')
      setGroupId('')
      setConfirmed(false)
      setGroups(null)
      onChanged()
    } catch (error) {
      setError(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Connect existing customer groups</CardTitle>
        <CardDescription>
          {missing.length} customer{missing.length === 1 ? '' : 's'} without an active group
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          Discover groups already joined by this phone. Verify customer representatives and relevant
          Xtellar staff before binding. Use a consistent name such as “Xtellar | Customer name”.
        </p>
        <Button variant="outline" disabled={busy || !ready} onClick={() => void discover()}>
          Discover existing groups
        </Button>
        {groups && availableGroups.length > 0 && (
          <section aria-label="Groups not linked to a customer yet" className="flex flex-col gap-2">
            <h3 className="text-sm font-medium">Groups not linked to a customer yet</h3>
            <ul className="divide-y rounded-md border text-sm">
              {availableGroups.map((group) => (
                <li
                  key={group.id}
                  className="flex flex-wrap items-center justify-between gap-2 p-3"
                >
                  <div className="min-w-0">
                    <p className="break-words font-medium">{group.name}</p>
                    <p className="break-all text-xs text-muted-foreground">
                      {group.id} · {group.participants.length} participant
                      {group.participants.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    aria-label={`Choose ${group.name} to bind`}
                    onClick={() => {
                      setGroupId(group.id)
                      setConfirmed(false)
                    }}
                  >
                    Choose to bind
                  </Button>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              Nothing from these groups is captured until one is bound to its customer below.
            </p>
          </section>
        )}
        {groups && (
          <FieldGroup>
            <Choice
              id="whatsapp-customer"
              label="Customer without a group"
              value={customerId}
              onChange={(value) => {
                setCustomerId(value)
                setConfirmed(false)
              }}
              placeholder="Choose customer"
              options={missing.map((customer) => ({
                id: customer.id,
                label: customer.company_name || customer.name,
              }))}
            />
            <Choice
              id="whatsapp-group"
              label="Existing WhatsApp group"
              value={groupId}
              onChange={(value) => {
                setGroupId(value)
                setConfirmed(false)
              }}
              placeholder="Choose an unbound group"
              options={availableGroups.map((group) => ({ id: group.id, label: group.name }))}
            />
            {selectedGroup && (
              <div className="flex flex-col gap-2 text-sm">
                <span className="break-all text-muted-foreground">
                  Group ID: {selectedGroup.id}
                </span>
                <span>Participants returned by WhatsApp:</span>
                <ul className="list-inside list-disc">
                  {selectedGroup.participants.map((person) => (
                    <li key={person.id}>
                      {person.id}
                      {person.role ? ` · ${person.role}` : ''}
                    </li>
                  ))}
                </ul>
                {!selectedGroup.participants.length && (
                  <p>
                    Participant details are unavailable. Verify the membership directly in WhatsApp
                    before confirming.
                  </p>
                )}
              </div>
            )}
            {!availableGroups.length && (
              <StatusNote>
                No unbound groups are available. Join the correct existing group from the phone,
                then discover again. If necessary, create a dedicated group manually and seed its
                background before binding.
              </StatusNote>
            )}
            <Field orientation="horizontal">
              <Checkbox
                id="whatsapp-notice"
                checked={confirmed}
                onCheckedChange={(value) => setConfirmed(value === true)}
              />
              <FieldLabel
                htmlFor="whatsapp-notice"
                className="[&>span:first-child]:whitespace-normal [&>span:first-child]:overflow-visible"
              >
                I verified the customer and staff participants and informed them about Helm capture.
              </FieldLabel>
            </Field>
            <Button
              disabled={busy || !validCustomer || !selectedGroup || !confirmed}
              onClick={() => void bind()}
            >
              Bind customer group
            </Button>
          </FieldGroup>
        )}
        <p className="text-sm text-muted-foreground">
          Binding starts capture from this point. Historical messages and reconnect recovery depend
          on WhatsApp availability; inspect coverage warnings below. New groups and invitations can
          be managed manually in WhatsApp.
        </p>
        {error && <StatusNote error>{error}</StatusNote>}
      </CardContent>
    </Card>
  )
}

function MessageRow({
  message,
  inquiries,
  consignments,
  onChanged,
}: {
  message: WhatsAppMessage
  inquiries: Inquiry[]
  consignments: Consignment[]
  onChanged: () => void
}) {
  const [inquiryId, setInquiryId] = useState(
    message.links.find((link) => link.inquiry_id)?.inquiry_id ?? 'unassigned',
  )
  const [consignmentId, setConsignmentId] = useState(
    message.links.find((link) => link.consignment_id)?.consignment_id ?? 'unassigned',
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  async function save() {
    setBusy(true)
    setError(null)
    try {
      await apiClient.linkWhatsAppMessage(message.id, {
        inquiry_id: inquiryId === 'unassigned' ? null : inquiryId,
        consignment_id: consignmentId === 'unassigned' ? null : consignmentId,
      })
      onChanged()
    } catch (error) {
      setError(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  async function attachment(id: string) {
    setError(null)
    try {
      const result = await apiClient.getAttachmentAccess(id, 'attachment')
      const url = new URL(result.access.url, window.location.href)
      if (!['https:', 'http:'].includes(url.protocol))
        throw new Error('Unsupported attachment URL.')
      window.open(url.href, '_blank', 'noopener,noreferrer')
    } catch (error) {
      setError(errorMessage(error))
    }
  }
  const sender =
    message.sender?.name || message.sender?.phone || message.sender?.id || 'Sender unavailable'
  return (
    <Card id={`whatsapp-message-${message.id}`} data-testid="whatsapp-message" tabIndex={-1}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <span>{sender}</span>
          <Badge variant="outline">{message.direction}</Badge>
          {message.metadata?.edited === true && (
            <Badge variant="secondary">Edited in WhatsApp</Badge>
          )}
          {message.metadata?.revoked === true && (
            <Badge variant="secondary">Removed in WhatsApp · original retained</Badge>
          )}
        </CardTitle>
        <CardDescription>{dateLabel(message.sent_at || message.created_at)}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="whitespace-pre-wrap break-words text-sm">
          {message.text_body || '(Message has no text)'}
        </p>
        {typeof message.metadata?.observed_text_body === 'string' && (
          <StatusNote>
            Provider history contains different text. Staff review required:{' '}
            {message.metadata.observed_text_body}
          </StatusNote>
        )}
        {message.sender?.id && (
          <p className="break-all text-xs text-muted-foreground">
            Sender reference: {message.sender.id}
          </p>
        )}
        <p className="break-all text-xs text-muted-foreground">Helm source message: {message.id}</p>
        {message.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {message.attachments.map((file) => (
              <Button
                key={file.id}
                variant="outline"
                size="sm"
                onClick={() => void attachment(file.id)}
              >
                {file.file_name || file.content_type}
              </Button>
            ))}
          </div>
        )}
        {!message.links.some((link) => link.inquiry_id || link.consignment_id) && (
          <Badge variant="secondary" className="self-start">
            {message.metadata?.routing_state === 'CUSTOMER_ONLY'
              ? 'Customer context only'
              : 'Needs staff matching'}
          </Badge>
        )}
        <FieldGroup>
          <Choice
            id={`inquiry-${message.id}`}
            label="Inquiry for message"
            value={inquiryId}
            onChange={setInquiryId}
            placeholder="Keep unassigned"
            options={[
              { id: 'unassigned', label: 'Keep unassigned' },
              ...inquiries.map((inquiry) => ({
                id: inquiry.id,
                label: inquiry.reference_code || inquiry.id,
              })),
            ]}
          />
          <Choice
            id={`consignment-${message.id}`}
            label="Consignment for message"
            value={consignmentId}
            onChange={setConsignmentId}
            placeholder="Keep unassigned"
            options={[
              { id: 'unassigned', label: 'Keep unassigned' },
              ...consignments.map((consignment) => ({
                id: consignment.id,
                label: `${consignment.code} · ${consignment.title}`,
              })),
            ]}
          />
          <Button variant="outline" disabled={busy} onClick={() => void save()}>
            Save message links
          </Button>
        </FieldGroup>
        {error && <StatusNote error>{error}</StatusNote>}
      </CardContent>
    </Card>
  )
}

function HumanComposer({
  binding,
  sendingEnabled,
  onQueued,
}: {
  binding: WhatsAppBinding
  sendingEnabled: boolean
  onQueued: () => void
}) {
  const [body, setBody] = useState('')
  const [approved, setApproved] = useState(false)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const request = useRef<{ body: string; key: string } | null>(null)
  const disabled = !sendingEnabled || !binding.active
  async function send() {
    if (!approved || !body.trim() || busy || disabled) return
    const text = body.trim()
    if (request.current?.body !== text) request.current = { body: text, key: crypto.randomUUID() }
    setBusy(true)
    setNote(null)
    setError(null)
    try {
      const result = await apiClient.queueWhatsAppReply(binding.id, {
        body: text,
        idempotency_key: request.current.key,
      })
      setBody('')
      setApproved(false)
      request.current = null
      setNote(
        result.status === 'UNKNOWN'
          ? 'Delivery unknown — inspect WhatsApp before sending again.'
          : result.status === 'SENT'
            ? 'The provider accepted this reply. Recipient delivery is not confirmed.'
            : 'Reply queued. Delivery is not yet confirmed.',
      )
      onQueued()
    } catch (error) {
      setError(
        error instanceof ApiClientError && error.status < 500
          ? error.message
          : 'Queue request outcome unknown. Refresh the outbox before retrying. Retrying unchanged text reuses the same request key.',
      )
    } finally {
      setBusy(false)
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Human reply to {binding.customer_name}</CardTitle>
        <CardDescription>
          This message goes to {binding.name}. Review the customer, group and wording before
          queueing.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {!sendingEnabled && (
          <StatusNote>Outbound sending is disabled in this environment.</StatusNote>
        )}
        {!binding.active && (
          <StatusNote>This binding is paused. Resume it before sending.</StatusNote>
        )}
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="whatsapp-reply">Message to customer group</FieldLabel>
            <Textarea
              id="whatsapp-reply"
              rows={4}
              value={body}
              disabled={disabled || busy}
              onChange={(event) => {
                setBody(event.target.value)
                setApproved(false)
              }}
            />
            <FieldDescription>
              No record changes or AI replies are applied by this composer.
            </FieldDescription>
          </Field>
          <Field orientation="horizontal">
            <Checkbox
              id="whatsapp-approve"
              checked={approved}
              disabled={disabled || busy}
              onCheckedChange={(value) => setApproved(value === true)}
            />
            <FieldLabel
              htmlFor="whatsapp-approve"
              className="[&>span:first-child]:whitespace-normal [&>span:first-child]:overflow-visible"
            >
              I reviewed this message and approve sending it to {binding.customer_name}.
            </FieldLabel>
          </Field>
          <Button
            disabled={disabled || busy || !approved || !body.trim()}
            onClick={() => void send()}
          >
            Queue reply to {binding.customer_name}
          </Button>
        </FieldGroup>
        {note && <StatusNote>{note}</StatusNote>}
        {error && <StatusNote error>{error}</StatusNote>}
      </CardContent>
    </Card>
  )
}

function CustomerConversation({
  binding,
  sendingEnabled,
  beaconEnabled,
  requestedMessageId,
}: {
  binding: WhatsAppBinding
  sendingEnabled: boolean
  beaconEnabled: boolean
  requestedMessageId?: string
}) {
  const navigationKey = useLocation().key
  const { data, error, loading, reload } = useAsyncData(async () => {
    const [messages, requested, outbox, inquiries, consignments] = await Promise.all([
      apiClient.listWhatsAppMessages(binding.id),
      requestedMessageId
        ? apiClient.listWhatsAppMessages(binding.id, undefined, requestedMessageId)
        : Promise.resolve([]),
      apiClient.listWhatsAppOutbox(binding.id),
      apiClient.listWhatsAppInquiryChoices(),
      apiClient.listWhatsAppConsignmentChoices(),
    ])
    return {
      messages: [
        ...new Map([...messages, ...requested].map((message) => [message.id, message])).values(),
      ],
      outbox,
      inquiries: inquiries.filter((item) => item.customer_id === binding.customer_id),
      consignments: consignments.filter((item) => item.customer_id === binding.customer_id),
    }
  }, [binding.id, binding.customer_id, requestedMessageId, navigationKey])
  const [older, setOlder] = useState<WhatsAppMessage[]>([])
  const [busy, setBusy] = useState(false)
  const [olderNote, setOlderNote] = useState<string | null>(null)
  const messages = [
    ...new Map(
      [...older, ...(data?.messages ?? [])].map((message) => [message.id, message]),
    ).values(),
  ].sort((a, b) => a.created_at.localeCompare(b.created_at))
  useEffect(() => {
    if (!requestedMessageId || loading || !data) return
    const target = document.getElementById(`whatsapp-message-${requestedMessageId}`)
    target?.scrollIntoView({ block: 'center' })
    target?.focus({ preventScroll: true })
  }, [requestedMessageId, loading, data])
  async function loadOlder() {
    const first = messages[0]
    if (!first) return
    setBusy(true)
    setOlderNote(null)
    try {
      const result = await apiClient.listWhatsAppMessages(binding.id, first.id)
      setOlder((previous) => [...result, ...previous])
      if (!result.length) setOlderNote('No earlier captured messages are available.')
    } catch (error) {
      setOlderNote(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  if (loading && !data) return <LoadingState label="Loading customer conversation..." />
  if (error instanceof ApiClientError && [401, 403, 404].includes(error.status))
    return <ErrorState error={error} onRetry={reload} />
  if (error && !data) return <ErrorState error={error} onRetry={reload} />
  return (
    <section
      className="flex min-w-0 flex-col gap-4"
      aria-label={`${binding.customer_name} conversation`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">{binding.customer_name} conversation</h2>
        <Button variant="outline" onClick={reload}>
          <RefreshCw data-icon="inline-start" />
          Refresh conversation and outbox
        </Button>
      </div>
      <StatusNote>
        Captured since {dateLabel(binding.capture_started_at)}. Coverage: {binding.coverage_status}.
        Last reconciliation: {dateLabel(binding.last_reconciled_at)}. WhatsApp history may be
        incomplete; review gaps against the phone.
      </StatusNote>
      {error && <StatusNote error>{error.message}</StatusNote>}
      {requestedMessageId &&
        !loading &&
        data &&
        !data.messages.some((message) => message.id === requestedMessageId) && (
          <StatusNote error>
            The requested message is unavailable in this customer conversation. No other customer
            group was searched.
          </StatusNote>
        )}
      {messages.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No messages captured for this group yet. Capture begins after binding; confirm the
          integration with internal test messages first.
        </p>
      )}
      {messages.length > 0 && (
        <Button variant="outline" disabled={busy} onClick={() => void loadOlder()}>
          Load earlier captured messages
        </Button>
      )}
      {olderNote && <StatusNote>{olderNote}</StatusNote>}
      {messages.map((message) => (
        <MessageRow
          key={message.id}
          message={message}
          inquiries={data?.inquiries ?? []}
          consignments={data?.consignments ?? []}
          onChanged={reload}
        />
      ))}
      {beaconEnabled ? (
        <WhatsAppBeaconReview
          binding={binding}
          sendingEnabled={sendingEnabled}
          inquiries={data?.inquiries ?? []}
          consignments={data?.consignments ?? []}
          onChanged={reload}
        />
      ) : (
        <p className="text-sm text-muted-foreground">
          Beacon review is disabled in this environment.
        </p>
      )}
      <HumanComposer binding={binding} sendingEnabled={sendingEnabled} onQueued={reload} />
      <Card>
        <CardHeader>
          <CardTitle>Recent outbound queue</CardTitle>
          <CardDescription>
            Queued and provider-accepted outcomes do not prove recipient delivery. Unknown outcomes
            require a manual WhatsApp check.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {data?.outbox.length === 0 && (
            <p className="text-sm text-muted-foreground">No outbound replies queued.</p>
          )}
          {data?.outbox.map((item: WhatsAppOutbox) => (
            <div key={item.id} className="flex flex-col gap-2 border-b pb-3 last:border-0">
              <Badge variant="outline" className="self-start">
                {item.status}
              </Badge>
              <p className="whitespace-pre-wrap break-words text-sm">{item.body}</p>
              {item.status === 'UNKNOWN' && (
                <StatusNote>Delivery unknown — inspect WhatsApp before sending again.</StatusNote>
              )}
              {item.last_error && (
                <p className="text-sm text-muted-foreground">{item.last_error}</p>
              )}
              <span className="text-xs text-muted-foreground">
                {dateLabel(item.created_at)} · {item.id}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>
    </section>
  )
}

/**
 * What a record owner who is not an administrator sees: their customers' groups and
 * Beacon's proposals for the records they own. No group history, pairing or binding.
 */
function OwnerReview({
  review,
  selectedBindingId,
  onSelect,
  onChanged,
}: {
  review: WhatsAppReviewGroups
  selectedBindingId: string | null
  onSelect: (bindingId: string) => void
  onChanged: () => void
}) {
  const selected =
    review.groups.find((group) => group.binding.id === selectedBindingId) ??
    (review.groups.length === 1 ? review.groups[0] : undefined)
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Beacon review for your customers</CardTitle>
          <CardDescription>
            Proposals for the inquiries and consignments you own, and replies to your customers'
            questions. Nothing changes or is sent until you approve it.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {review.groups.map((group) => (
            <div
              key={group.binding.id}
              className="flex flex-wrap items-center justify-between gap-2 border-b pb-3 last:border-0"
            >
              <div className="min-w-0">
                <p className="font-medium">{group.binding.customer_name}</p>
                <p className="break-words text-sm text-muted-foreground">{group.binding.name}</p>
              </div>
              <Button
                variant="outline"
                aria-label={`Review ${group.binding.customer_name}`}
                onClick={() => onSelect(group.binding.id)}
              >
                Review proposals
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
      {selected && (
        <WhatsAppBeaconReview
          key={selected.binding.id}
          binding={{
            ...selected.binding,
            group_id: '',
            capture_started_at: null,
            last_reconciled_at: null,
            coverage_status: '',
            last_error: null,
          }}
          sendingEnabled={review.sending_enabled}
          inquiries={selected.inquiries as unknown as Inquiry[]}
          consignments={selected.consignments as unknown as Consignment[]}
          onChanged={onChanged}
          ownerView
        />
      )}
    </>
  )
}

export function WhatsAppPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { data, error, loading, reload } = useAsyncData(async () => {
    const profile = await apiClient.getProfile()
    if (!profile.is_admin && profile.role !== 'admin') {
      // A record owner reviews Beacon's proposals for their own customers' groups.
      const review = await apiClient.listWhatsAppReviewGroups().catch(() => null)
      return { admin: false as const, review }
    }
    const [status, bindings, customers] = await Promise.all([
      apiClient.getWhatsAppStatus(),
      apiClient.listWhatsAppBindings(),
      apiClient.listWhatsAppCustomerChoices(),
    ])
    return { admin: true as const, status, bindings, customers }
  }, [])
  const selectedBindingId = searchParams.get('binding_id')
  const requestedMessageId = searchParams.get('message_id') ?? undefined
  const [actionError, setActionError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const digestKeys = useRef<Record<string, string>>({})
  const { confirm, dialog } = useConfirm()
  if (loading && !data) return <LoadingState label="Loading WhatsApp settings..." />
  if (error && !data) return <ErrorState error={error} onRetry={reload} />
  const selectedBinding = data?.admin
    ? data.bindings.find((binding) => binding.id === selectedBindingId)
    : null
  async function bindingAction(
    binding: WhatsAppBinding,
    action: 'toggle' | 'reconcile' | 'digest',
  ) {
    if (
      action === 'toggle' &&
      !(await confirm({
        title: binding.active ? 'Pause this customer group?' : 'Resume this customer group?',
        description: binding.active
          ? 'Helm capture and outbound replies will pause. Messages sent during a pause may not be recoverable.'
          : 'Verify the participants and participant notice in WhatsApp before resuming capture.',
        action: binding.active ? 'Pause capture' : 'Resume capture',
      }))
    )
      return
    setBusy(true)
    setActionError(null)
    setNotice(null)
    try {
      if (action === 'toggle') await apiClient.updateWhatsAppBinding(binding.id, !binding.active)
      else if (action === 'digest') {
        const key = (digestKeys.current[binding.id] ??= crypto.randomUUID())
        await apiClient.requestWhatsAppBeaconDigest(binding.id, { idempotency_key: key })
        delete digestKeys.current[binding.id]
        setNotice(
          `Beacon is reading ${binding.customer_name}'s new messages now. Refresh in a minute; proposals still need review and nothing is sent.`,
        )
      } else {
        await apiClient.reconcileWhatsAppBinding(binding.id)
        setNotice(
          'Reconciliation queued. Refresh after the worker completes; this cannot guarantee recovery of every missing message.',
        )
      }
      reload()
    } catch (error) {
      setActionError(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="helm-private flex min-w-0 flex-col gap-6">
      <PageHeader
        title="WhatsApp"
        description="Connect the business phone and manage a dedicated group for each customer"
        breadcrumbs={[{ label: 'Settings', href: '/settings' }, { label: 'WhatsApp' }]}
      />
      {!data?.admin ? (
        data?.review?.groups.length ? (
          <OwnerReview
            review={data.review}
            selectedBindingId={selectedBindingId}
            onSelect={(id) => setSearchParams({ binding_id: id })}
            onChanged={reload}
          />
        ) : (
          <Alert>
            <ShieldCheck className="size-4" />
            <AlertDescription>Admin access is required to manage WhatsApp.</AlertDescription>
          </Alert>
        )
      ) : (
        <>
          {error && <StatusNote error>{error.message}</StatusNote>}
          <SessionCard status={data.status} onChanged={reload} />
          <GroupProvisioning
            customers={data.customers}
            bindings={data.bindings}
            ready={
              data.status.enabled && data.status.configured && data.status.status === 'WORKING'
            }
            onChanged={reload}
          />
          <Card>
            <CardHeader>
              <CardTitle>Customer groups</CardTitle>
              <CardDescription>
                One active group per customer. Inquiries and consignments share the customer
                conversation.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {data.bindings.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No customer groups are connected yet.
                </p>
              )}
              {data.bindings.map((binding) => (
                <div key={binding.id} className="flex flex-col gap-3 border-b pb-4 last:border-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      className="font-medium underline underline-offset-4"
                      to={`/customers/${binding.customer_id}`}
                    >
                      {binding.customer_name}
                    </Link>
                    <Badge variant={binding.active ? 'secondary' : 'outline'}>
                      {binding.active ? 'Active' : 'Paused'}
                    </Badge>
                    <Badge variant="outline">{binding.coverage_status}</Badge>
                  </div>
                  <p className="break-all text-sm text-muted-foreground">
                    {binding.name} · {binding.group_id}
                  </p>
                  {binding.last_error && <StatusNote error>{binding.last_error}</StatusNote>}
                  {data.status.beacon_enabled === true && <BeaconDigestStatus binding={binding} />}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setSearchParams({ binding_id: binding.id })}
                      aria-label={`Open ${binding.customer_name} conversation`}
                    >
                      Open conversation
                    </Button>
                    {data.status.beacon_enabled === true &&
                      binding.active &&
                      binding.beacon_digest?.active_group === true && (
                        <Button
                          variant="outline"
                          disabled={busy}
                          aria-label={`Read ${binding.customer_name}'s new messages now`}
                          onClick={() => void bindingAction(binding, 'digest')}
                        >
                          Read new messages now
                        </Button>
                      )}
                    <Button
                      variant="outline"
                      disabled={busy || !binding.active}
                      onClick={() => void bindingAction(binding, 'reconcile')}
                    >
                      Reconcile captured history
                    </Button>
                    <Button
                      variant="outline"
                      disabled={busy}
                      onClick={() => void bindingAction(binding, 'toggle')}
                    >
                      {binding.active ? 'Pause capture' : 'Resume capture'}
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
          {actionError && <StatusNote error>{actionError}</StatusNote>}
          {notice && <StatusNote>{notice}</StatusNote>}
          {selectedBindingId && !selectedBinding && (
            <StatusNote error>
              The requested WhatsApp conversation is unavailable. No other customer group was
              opened.
            </StatusNote>
          )}
          {selectedBinding && (
            <CustomerConversation
              key={selectedBinding.id}
              binding={selectedBinding}
              sendingEnabled={data.status.sending_enabled}
              beaconEnabled={data.status.beacon_enabled === true}
              requestedMessageId={requestedMessageId}
            />
          )}
        </>
      )}
      {dialog}
    </div>
  )
}
